import { memo, useEffect, useRef, useState } from "react";
import { ContactEditProps } from "./types";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";
import { initialContactEditForm } from "services/api/contacts/consts";
import { CONTACT_EDIT_FORM_SCHEMA } from "./validation";
import {
  createContactAPI,
  getContactAPI,
  updateContactAPI,
  publishContactAPI,
} from "services/api/contacts/api";
import { Contact, ContactEditFormValues } from "services/api/contacts/types";
import { transformContactEditFormValues } from "services/api/contacts/transformations";
import { Wizard } from "ui/components/Wizard";
import WizardStep from "ui/components/Wizard/WizardStep";
import WizardFooter from "ui/components/Wizard/WizardFooter";
import { Loader } from "ui/components/Loader";
import ContactInfoStep from "./components/ContactInfoStep/ContactInfoStep";
import ContactPictureStep from "./components/ContactPictureStep/ContactPictureStep";
import ContactEmailStep from "./components/ContactEmailStep/ContactEmailStep";
import ContactPhoneStep from "./components/ContactPhoneStep/ContactPhoneStep";
import ContactAddressStep from "./components/ContactAddressStep/ContactAddressStep";
import ContactNoteStep from "./components/ContactNoteStep/ContactNoteStep";
import ContactRepresentativeStep from "./components/ContactRepresentativeStep/ContactRepresentativeStep";
import ContactStepStatusBar from "./components/ContactStepStatusBar/ContactStepStatusBar";
import ContactSummaryStep from "./components/ContactSummaryStep/ContactSummaryStep";
import ContactWizardIntro from "./components/ContactWizardIntro/ContactWizardIntro";


const ContactEditCard: React.FC<ContactEditProps> = (props) => {
  const { contactId, show, onSuccess, onClose, isFullScreen = true, initialContactTypeId, lockContactType, initialContactGroupIds, initialName, initialOib } = props;

  // Tvrtka je primarni tip kontakta — na nju se pada i kad zadnji odabir nije zapamćen.
  const getDefaultContactTypeId = () => {
    if (initialContactTypeId) return initialContactTypeId;
    return localStorage.getItem("contact-last-type") ?? "C";
  };

  /**
   * Upisani naziv raspoređen po poljima koja vrsta kontakta traži: tvrtki naziv, osobi
   * ime i prezime (prva riječ je ime, ostatak prezime).
   */
  const getInitialNameValues = (contactTypeId: string) => {
    const name = initialName?.trim();
    if (!name) return {};

    if (contactTypeId === "C") return { companyName: name };

    const [firstName, ...rest] = name.split(" ");

    return { firstName, lastName: rest.join(" ") };
  };

  const buildInitialFormValues = (): ContactEditFormValues => {
    const contactTypeId = getDefaultContactTypeId();

    return {
      ...initialContactEditForm,
      contactTypeId,
      contactGroupIds: initialContactGroupIds ?? initialContactEditForm.contactGroupIds,
      personalIdentificationNumber: initialOib?.trim() || initialContactEditForm.personalIdentificationNumber,
      ...getInitialNameValues(contactTypeId),
    };
  };

  const [formValues, setFormValues] = useState<ContactEditFormValues>(buildInitialFormValues);
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [stepCurrent, setStepCurrent] = useState(0);
  const showIntro = isFullScreen;
  const title = contactId ? "Ažuriranje kontakta" : "Unos kontakta";
  const isProcessingRef = useRef(false);

  useEffect(() => {
    if (show && contactId) {
      fetchContact(contactId);
    }
    if (show && !contactId) {
      setFormValues(buildInitialFormValues());
      setStepCurrent(0);
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, contactId, initialName]);

  // Promjena vrste seli upisani naziv u polja koja ta vrsta traži — ali samo dok su
  // prazna, da se ne prepiše ono što je korisnik u međuvremenu utipkao.
  useEffect(() => {
    if (!show || contactId || !initialName?.trim()) return;

    setFormValues((prev) => {
      const isCompany = prev.contactTypeId === "C";
      const isEmpty = isCompany
        ? !prev.companyName
        : !prev.firstName && !prev.lastName;

      if (!isEmpty) return prev;

      return { ...prev, ...getInitialNameValues(prev.contactTypeId ?? "C") };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, contactId, initialName, formValues.contactTypeId]);

  const fetchContact = async (id: string) => {
    setIsFetching(true);
    try {
      const result = await getContactAPI(id);
      setFormValues(transformContactEditFormValues(result));
      setErrors({});
    } catch {
      // error handled by snackbar
    }
    setIsFetching(false);
  };

  const saveContact = async () => {
    setIsLoading(true);
    try {
      const id = contactId || formValues.id;
      let result: Contact | undefined;

      const uploads = (formValues.pictures ?? []).flatMap(p =>
        p.files.map(f => ({
          id: f.id,
          originalFileName: f.name,
          fileExtension: f.name.split(".").pop() ?? "",
          fileSizeKb: Math.round(f.size / 1024),
          contentType: f.type,
          created: new Date(),
          url: f.url ?? "",
        }))
      );

      if (!!id) {
        result = await updateContactAPI(id, formValues, uploads);
      } else {
        result = await createContactAPI(formValues, uploads);
      }

      if (result) {
        setFormValues(prev => ({ ...prev, id: result!.id }));
        if (!id && formValues.contactTypeId) {
          localStorage.setItem("contact-last-type", formValues.contactTypeId);
        }
      }
    } catch (e) {
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleNextStep = async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    try {
      if (stepCurrent === 0) {
        const isValid = validateYup(formValues, CONTACT_EDIT_FORM_SCHEMA, setErrors);
        if (!isValid) return;
      }

      await saveContact();
      setStepCurrent((s) => s + 1);
    } finally {
      isProcessingRef.current = false;
    }
  };

  const handleBackStep = async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    try {
      setStepCurrent((s) => s - 1);
    } finally {
      isProcessingRef.current = false;
    }
  };

  const publishContact = async () => {
    setIsLoading(true);
    try {
      const id = contactId || formValues.id;
      if (!!id) {
        const result = await publishContactAPI(id);
        if (result && onSuccess) {
          setFormValues(initialContactEditForm);
          setStepCurrent(0);
          onSuccess(id);
        }
      }
    } catch (e) {
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const isCompany = formValues.contactTypeId === "C";

  const stepLabels = (() => {
    const labels = ["Detalji"];
    if (isCompany) labels.push("Osobe");
    labels.push("Email", "Telefon", "Adrese", "Bilješke", "Slike", "Pregled");
    return labels;
  })();

  // Klik na korak u traci radi što i "Nastavi": s Detalja se prvo validira, pa se kontakt
  // spremi (i slike), tek onda prelazi — inače bi se izmjene trenutnog koraka izgubile.
  const handleStepClick = async (stepIndex: number) => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    try {
      if (stepCurrent === 0) {
        const isValid = validateYup(formValues, CONTACT_EDIT_FORM_SCHEMA, setErrors);
        if (!isValid) return;
      }

      await saveContact();
      setStepCurrent(stepIndex);
    } finally {
      isProcessingRef.current = false;
    }
  };

  // "Uredi" u pregledu vodi na korak po nazivu — redni broj ovisi o tome je li kontakt tvrtka.
  const handleEditStep = (stepLabel: string) => {
    const stepIndex = stepLabels.indexOf(stepLabel);
    if (stepIndex >= 0) setStepCurrent(stepIndex);
  };

  const firstStepCompleted = !!(contactId || formValues.id);

  const statusBar = (
    <ContactStepStatusBar
      stepLabels={stepLabels}
      stepCurrent={stepCurrent}
      onStepClick={handleStepClick}
      firstStepCompleted={firstStepCompleted}
    />
  );

  const buildSteps = () => {
    const result = [];
    let stepIndex = 0;

    result.push(
      <WizardStep key="info" stepNumber={stepIndex} stepCurrent={stepCurrent}>
        {statusBar}
        <ContactInfoStep formValues={formValues} setFormValues={setFormValues} errors={errors} setErrors={setErrors} showContactType={!lockContactType} />
      </WizardStep>
    );
    stepIndex++;

    if (isCompany) {
      result.push(
        <WizardStep key="representatives" stepNumber={stepIndex} stepCurrent={stepCurrent}>
          {statusBar}
          <ContactRepresentativeStep formValues={formValues} />
        </WizardStep>
      );
      stepIndex++;
    }

    result.push(
      <WizardStep key="emails" stepNumber={stepIndex} stepCurrent={stepCurrent}>
        {statusBar}
        <ContactEmailStep formValues={formValues} />
      </WizardStep>
    );
    stepIndex++;

    result.push(
      <WizardStep key="phones" stepNumber={stepIndex} stepCurrent={stepCurrent}>
        {statusBar}
        <ContactPhoneStep formValues={formValues} />
      </WizardStep>
    );
    stepIndex++;

    result.push(
      <WizardStep key="addresses" stepNumber={stepIndex} stepCurrent={stepCurrent}>
        {statusBar}
        <ContactAddressStep formValues={formValues} />
      </WizardStep>
    );
    stepIndex++;

    result.push(
      <WizardStep key="notes" stepNumber={stepIndex} stepCurrent={stepCurrent}>
        {statusBar}
        <ContactNoteStep formValues={formValues} />
      </WizardStep>
    );
    stepIndex++;

    result.push(
      <WizardStep key="pictures" stepNumber={stepIndex} stepCurrent={stepCurrent}>
        {statusBar}
        <ContactPictureStep formValues={formValues} setFormValues={setFormValues} />
      </WizardStep>
    );
    stepIndex++;

    result.push(
      <WizardStep key="summary" stepNumber={stepIndex} stepCurrent={stepCurrent}>
        {statusBar}
        <ContactSummaryStep formValues={formValues} onEditStep={handleEditStep} />
      </WizardStep>
    );

    return result;
  };

  const steps = buildSteps();

  return (
    <Modal
      show={show}
      onHide={onClose}
      size={isFullScreen ? "full" : "medium"}
      position={isFullScreen ? "side" : "centered"}
      intro={isFullScreen && showIntro ? {
        content: (
          <ContactWizardIntro
            formValues={formValues}
            stepCurrent={stepCurrent}
            stepLabels={stepLabels}
            isEdit={!!contactId}
          />
        ),
      } : undefined}>
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={title}
        onHide={onClose} />
      <ModalBody>
        {isFetching ? (
          <div className="relative min-h-[60vh] xl:min-h-[600px] h-[calc(100vh-160px)]">
            <Loader isLoading={true}>{null}</Loader>
          </div>
        ) : (
          <Wizard steps={steps} />
        )}
      </ModalBody>
      <WizardFooter
        stepCurrent={stepCurrent}
        steps={steps}
        onNextStep={handleNextStep}
        onBackStep={handleBackStep}
        onFinish={publishContact}
        isLoading={isLoading}
        isReadOnly={false}
      />
    </Modal>
  );
};

export default memo(ContactEditCard);
